import { Link, useRouter } from "@/lib/nav";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import { FilterBar, SearchField } from "@/components/filter-bar";
import FilterChips from "@/components/filter-chips";
import Field from "@/components/field";
import SaveBar from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import ListTable from "@buildpy/ui/list-table";
import InfoTip from "@buildpy/ui/info-tip";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { EMPTY_MARK, formatDate, formatNumber, formatWon } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { cn } from "@/lib/utils";
import { settleRedirect } from "@/lib/redirect-error";
import { PriceEditRow } from "@buildpy/core/modules/org/action-types";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import { vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import { commitMarginText } from "@buildpy/core/modules/quote/editor/sheet/cell-edit";
import {
  appliedMarginBp,
  costTotalOf,
  DEFAULT_QUOTE_MARGIN_BP,
  isMarginBp,
  MarginSettings,
  PRICE_INPUT_MAX,
  PricedSale,
  pricedSaleOrStored,
  profitRateOf,
  SALE_OVER_MAX_MESSAGE,
  saleOverMax,
} from "@buildpy/core/modules/template/price-margin";
import { savePriceSettings } from "@/api/actions/org";
import { usePhone } from "@/modules/quote/ui/editor/mobile/use-phone";
import { parseMoney } from "@/modules/quote/ui/format";
import { resolveTradeName, TradeCode } from "@buildpy/core/modules/template/trades";
import { catalogLinkFilterOf } from "@buildpy/core/modules/catalog/rules";
import type { CatalogLinkFilter, CatalogLinkStatus } from "@buildpy/core/modules/catalog/types";
import CatalogPriceCell, { useCatalogRunner } from "@/modules/org/ui/catalog-price-cell";
import CatalogSubmitDialog, { type CatalogSubmitTarget } from "@/modules/org/ui/catalog-submit-dialog";

export interface PriceRow {
  id: string;
  trade: TradeCode;
  scope: string;
  item: string;
  unit: string;
  material_unit_price: number;
  labor_unit_price: number;
  material_cost?: number | null;
  labor_cost?: number | null;
  margin_bp?: number | null;
  used_count: number;
  last_used_at: string | null;
  // 표준 상품 상태 — 없으면 미연결로 본다.
  catalog?: { status: CatalogLinkStatus; product: { id: string; name: string } | null };
}

// 「연결」 거르개 개수 — 미연결은 연결 제안을 포함한다.
export interface CatalogCounts {
  all: number;
  linked: number;
  pending: number;
  suggested: number;
  unlinked: number;
  optOut: number;
}

const LINK_FILTER_LABEL: Record<CatalogLinkFilter, string> = { all: "전체", linked: "표준 연결", unlinked: "미연결", pending: "검수 대기" };

// 거르개 주소 — 공종·검색은 그대로 두고 연결만 바꾼다.
export const priceFilterHref = (filter: { trade: string; q: string }, link: CatalogLinkFilter): string => {
  const params = new URLSearchParams({ tab: "prices" });
  if (filter.trade) params.set("trade", filter.trade);
  if (filter.q) params.set("q", filter.q);
  if (link !== "all") params.set("link", link);
  return `/app/settings?${params.toString()}`;
};

/** 공종 고르개·표기에 쓰는 목록. 「빌드파이 표준 + 이 회사」 를 서버가 합쳐 준다. */
export interface TradeOption {
  code: string;
  name: string;
}

interface Props {
  rows: PriceRow[];
  trades: TradeOption[];
  filter: { trade: string; q: string; link?: string };
  canEdit: boolean;
  // 마진 설정은 대표만(org_setting 쓰기) — 안 주면 canEdit 을 따른다.
  canEditMargins?: boolean;
  // 회사 기본·공종별 마진 — 없으면 기본 +20%.
  margins?: MarginSettings;
  catalogCounts?: CatalogCounts;
}

// 한 줄의 고친 값 — 원가·품목 마진(비우면 null)과 원가가 없을 때 쓰는 판매가(공급가).
export interface PriceDraft {
  material_cost: number | null;
  labor_cost: number | null;
  margin_bp: number | null;
  material: number;
  labor: number;
}

/** 고친 값만 담는다 — 한 줄도 안 건드렸으면 빈 객체다. */
type Edits = Record<string, PriceDraft>;

// 「품목 추가」 로 만든 새 줄 — 저장하면 단가표에 쌓인다.
interface NewRow extends PriceDraft {
  key: string;
  trade: string;
  scope: string;
  item: string;
  unit: string;
}

const NO_MARGINS: MarginSettings = { default_bp: DEFAULT_QUOTE_MARGIN_BP, trades: {} };

export const draftOf = (row: PriceRow): PriceDraft => ({
  material_cost: row.material_cost ?? null,
  labor_cost: row.labor_cost ?? null,
  margin_bp: row.margin_bp ?? null,
  material: row.material_unit_price,
  labor: row.labor_unit_price,
});

// 판매가 — 원가가 있으면 원가 × (1 + 적용 마진), 없으면 적은 값 · 보기 방식(별도·포함)으로 바꿔 보인다 · 범위를 넘으면 저장된 단가.
export const saleOf = (trade: string, draft: PriceDraft, margins: MarginSettings, vatMode: VatMode): PricedSale =>
  pricedSaleOrStored(
    {
      trade,
      material_unit_price: draft.material,
      labor_unit_price: draft.labor,
      material_cost: draft.material_cost,
      labor_cost: draft.labor_cost,
      margin_bp: draft.margin_bp,
    },
    margins,
    vatMode
  );

// 저장할 만큼 바뀌었나 — 원가가 있는 줄의 판매가는 계산값이라 원가·마진만 본다.
export const draftChanged = (row: PriceRow, draft: PriceDraft): boolean => {
  const base = draftOf(row);
  return (
    draft.material_cost !== base.material_cost ||
    draft.labor_cost !== base.labor_cost ||
    draft.margin_bp !== base.margin_bp ||
    (costTotalOf(draft) === null && (draft.material !== base.material || draft.labor !== base.labor))
  );
};

export const sameMargins = (a: MarginSettings, b: MarginSettings): boolean => {
  const keys = Object.keys(a.trades);
  return (
    a.default_bp === b.default_bp &&
    keys.length === Object.keys(b.trades).length &&
    keys.every((code) => a.trades[code] === b.trades[code])
  );
};

// 만분율 → 「20」·「12.5」 (% 칸 글자).
export const bpText = (bp: number): string => String(Number((bp / 100).toFixed(2)));

// 이익률 = 마진 ÷ (1 + 마진) → 「16.7」.
export const profitText = (bp: number): string => {
  const rate = profitRateOf(bp);
  return rate === null ? "" : String(Number((rate * 100).toFixed(1)));
};

const signed = (bp: number): string => `${bp >= 0 ? "+" : ""}${bpText(bp)}%`;

// 원가 칸 읽기 — 비우면 원가 없음(null).
const costInput = (text: string): number | null => (text.replace(/[^\d]/g, "") === "" ? null : parseMoney(text));

// 저장 상한을 넘는 입력은 받지 않는다 — 칸은 이전 값 그대로.
export const overPriceMax = (value: number | null): boolean => value !== null && value > PRICE_INPUT_MAX;

// 원가 × 마진 판매가가 단가 상한을 넘나 — 서버 저장 검사(saleOverMax)와 같은 자.
export const saleTooBig = (trade: string, draft: PriceDraft, margins: MarginSettings): boolean =>
  saleOverMax(
    { trade, material_unit_price: draft.material, labor_unit_price: draft.labor, material_cost: draft.material_cost, labor_cost: draft.labor_cost, margin_bp: draft.margin_bp },
    margins
  );

export interface OverflowEntry {
  trade: string;
  item: string;
  draft: PriceDraft;
}

// 저장하면 상한을 넘는 첫 항목 — 고친 줄은 그대로, 안 고친 줄은 이번 마진 변경으로 넘게 된 것만(서버와 같은 규칙).
export const overflowItemOf = (edited: OverflowEntry[], untouched: OverflowEntry[], next: MarginSettings, saved: MarginSettings): string | null => {
  const hit =
    edited.find((row) => saleTooBig(row.trade, row.draft, next)) ??
    untouched.find((row) => saleTooBig(row.trade, row.draft, next) && !saleTooBig(row.trade, row.draft, saved));
  return hit ? hit.item || hit.trade : null;
};

export const saleOverMaxText = (item: string): string => `${SALE_OVER_MAX_MESSAGE} — ${item}`;

interface MarginInputProps {
  bp: number | null;
  label: string;
  name: string;
  disabled: boolean;
  placeholder?: string;
  // 비우기 허용(품목 마진) — 회사 기본·공종 마진은 비울 수 없다.
  allowEmpty: boolean;
  inputSize: "xs" | "default";
  onChange: (bp: number | null) => void;
  // 받으면 안 되는 값(판매가 상한 넘김)의 문구 — 있으면 올리지 않고 칸에 보인다.
  reject?: (bp: number | null) => string | null;
}

// % 칸 — 적는 동안은 글자를 쥐고, 읽을 수 있는 값만 올린다 · 빠져나오면 마지막 값으로 돌아간다.
const MarginInput = ({ bp, label, name, disabled, placeholder, allowEmpty, inputSize, onChange, reject }: MarginInputProps) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Input
      aria-label={label}
      className="text-right tabular-nums"
      disabled={disabled}
      errorMessage={inputSize === "default" ? (error ?? undefined) : undefined}
      aria-invalid={error ? true : undefined}
      inputMode="decimal"
      inputSize={inputSize}
      name={name}
      placeholder={placeholder}
      title={error ?? undefined}
      value={text ?? (bp === null ? "" : bpText(bp))}
      onBlur={() => {
        setText(null);
        setError(null);
      }}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        const commit = (value: number | null) => {
          const rejected = reject?.(value) ?? null;
          setError(rejected);
          if (rejected === null) onChange(value);
        };
        if (next.trim() === "" && allowEmpty) {
          commit(null);
          return;
        }
        const parsed = commitMarginText(next);
        const value = parsed.ok ? Math.round(parsed.margin * 10_000) : null;
        if (value !== null && isMarginBp(value)) {
          commit(value);
        } else {
          setError("마진은 -90% ~ +1000% 숫자로 적어 주세요");
        }
      }}
    />
  );
};

// 폰 접기 묶음 — 공종 목록 순서, 목록에 없는 코드는 뒤에 처음 나온 순서로.
export const groupByTrade = (rows: PriceRow[], trades: TradeOption[]): { trade: string; rows: PriceRow[] }[] => {
  const byTrade = new Map<string, PriceRow[]>();
  for (const row of rows) byTrade.set(row.trade, [...(byTrade.get(row.trade) ?? []), row]);
  const known = trades.map((trade) => trade.code).filter((code) => byTrade.has(code));
  const rest = [...byTrade.keys()].filter((code) => !known.includes(code));
  return [...known, ...rest].map((trade) => ({ trade, rows: byTrade.get(trade) ?? [] }));
};

type Folds = { key: string; open: Record<string, boolean> };
export const filterKey = (filter: { trade: string; q: string }): string => JSON.stringify([filter.trade, filter.q]);
// 손으로 접은 기록은 그 기록을 남긴 필터에서만 쓴다 — 검색 뒤 일치 줄이 접힌 채 숨지 않게.
export const foldsFor = (folds: Folds, key: string): Record<string, boolean> => (folds.key === key ? folds.open : {});

/**
 * 단가표.
 *
 * 줄마다 여는 편집 시트를 두지 않는다 — 단가 손보기는 「여러 줄을 훑으며 조금씩」
 * 이라 칸 안에서 바로 고치고 한 번에 저장하는 편이 손이 적다.
 * 공종·검색은 URL 쿼리라 필터를 걸어 둔 화면이 새로고침에 살아남는다.
 */
const SettingsPrices = ({ rows, trades, filter, canEdit, canEditMargins = canEdit, margins: savedMargins = NO_MARGINS, catalogCounts }: Props) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [edits, setEdits] = useState<Edits>({});
  const [added, setAdded] = useState<NewRow[]>([]);
  // 고친 마진 설정 — 안 건드렸으면 null(저장된 값).
  const [marginDraft, setMarginDraft] = useState<MarginSettings | null>(null);
  // 판매가 보기 — 저장값은 늘 공급가, 포함은 ×1.1 해서 보이기만 한다.
  const [vatView, setVatView] = useState<VatMode>("EXCL");
  // 서버·첫 그림은 null 이라 두 벌 다 그리고 CSS 가 가른다 — 그 뒤로는 한 벌만 남긴다.
  const phone = usePhone();
  // 폰 공종 묶음을 손으로 연·닫은 기록 — 검색·공종 필터가 바뀌면 버리고 기본값(expandAll·바뀜)으로 돌아간다.
  const [folds, setFolds] = useState<Folds>({ key: "", open: {} });
  const foldKey = filterKey(filter);
  const openTrades = foldsFor(folds, foldKey);
  // 표준 상품 — 검수 요청 대화상자 대상 · 연결 제안 알림 닫기 · 연결·품목만 저장 중.
  const [submitTarget, setSubmitTarget] = useState<CatalogSubmitTarget | null>(null);
  const [suggestHidden, setSuggestHidden] = useState(false);
  const { busy: catalogBusy, run: runCatalog } = useCatalogRunner();
  const linkFilter = catalogLinkFilterOf(filter.link);

  const margins = marginDraft ?? savedMargins;
  const marginDirty = marginDraft !== null && !sameMargins(marginDraft, savedMargins);
  const editsOn = !canEdit || pending;
  const marginsOff = !canEditMargins || pending;

  const changed = useMemo(
    () =>
      rows.filter((row) => {
        const edit = edits[row.id];
        return edit !== undefined && draftChanged(row, edit);
      }),
    [rows, edits]
  );
  const changedIds = useMemo(() => new Set(changed.map((row) => row.id)), [changed]);
  const dirty = changed.length > 0 || added.length > 0 || marginDirty;
  const editedEntries: OverflowEntry[] = [
    ...changed.map((row) => ({ trade: row.trade, item: row.item, draft: edits[row.id] ?? draftOf(row) })),
    ...added.map((row) => ({ trade: row.trade, item: row.item, draft: row })),
  ];
  const untouchedEntries: OverflowEntry[] = rows.filter((row) => !changedIds.has(row.id)).map((row) => ({ trade: row.trade, item: row.item, draft: edits[row.id] ?? draftOf(row) }));
  // 마진 칸 검사 — 보이는 줄만 본다(필터 밖 줄은 서버가 막는다).
  const marginReject = (next: MarginSettings): string | null => {
    const item = overflowItemOf(editedEntries, untouchedEntries, next, savedMargins);
    return item === null ? null : saleOverMaxText(item);
  };
  const overItem = overflowItemOf(editedEntries, marginDirty ? untouchedEntries : [], margins, savedMargins);
  const blocked = added.some((row) => row.item.trim() === "")
    ? "새 품목의 항목을 적어 주세요."
    : overItem !== null
      ? `${saleOverMaxText(overItem)}.`
      : undefined;

  const groups = useMemo(() => groupByTrade(rows, trades), [rows, trades]);
  // 검색·공종을 걸었거나 묶음이 하나면 처음부터 펼친다 — 찾은 줄이 접혀 숨지 않게.
  const expandAll = filter.q !== "" || filter.trade !== "" || groups.length === 1;

  const draftFor = (row: PriceRow): PriceDraft => edits[row.id] ?? draftOf(row);

  const onEdit = (row: PriceRow, patch: Partial<PriceDraft>) =>
    setEdits((prev) => ({ ...prev, [row.id]: { ...(prev[row.id] ?? draftOf(row)), ...patch } }));

  const onEditAdded = (key: string, patch: Partial<NewRow>) =>
    setAdded((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const onAddRow = () =>
    setAdded((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}-${prev.length}`,
        trade: filter.trade || (trades[0]?.code ?? ""),
        scope: "",
        item: "",
        unit: "",
        material_cost: null,
        labor_cost: null,
        margin_bp: null,
        material: 0,
        labor: 0,
      },
    ]);

  const setMargins = (next: MarginSettings) => setMarginDraft(next);

  const toEdit = (key: { trade: string; scope: string; item: string; unit: string }, draft: PriceDraft): PriceEditRow => {
    const sale = saleOf(key.trade, draft, margins, "EXCL");
    return {
      trade: key.trade,
      scope: key.scope,
      item: key.item,
      unit: key.unit,
      material_unit_price: sale.material_unit_price,
      labor_unit_price: sale.labor_unit_price,
      material_cost: draft.material_cost,
      labor_cost: draft.labor_cost,
      margin_bp: draft.margin_bp,
    };
  };

  const onSave = () => {
    const payload: PriceEditRow[] = [
      ...changed.map((row) => toEdit(row, draftFor(row))),
      ...added.map((row) => toEdit(row, row)),
    ];
    const nextMargins = marginDirty ? margins : null;

    startTransition(async () => {
      try {
        // 마진과 줄을 한 번에 — 둘로 부르면 사이에서 끊겼을 때 마진만 바뀐 채 남는다(rpc_price_settings_save).
        const result = await savePriceSettings({ margins: nextMargins, rows: payload });
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        setMarginDraft(null);
        setEdits({});
        setAdded([]);
        toast.success(result.message);
        router.refresh();
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  // 품목 마진이 비었을 때 보이는 회색 글자 — 공종 → 회사 기본.
  const fallbackText = (trade: string): string => `기본 ${signed(appliedMarginBp({ trade, margin_bp: null }, margins))}`;

  // 원가 두 칸(자재·인건) — 비우면 원가 없음.
  const costInputs = (trade: string, label: string, name: string, draft: PriceDraft, onPatch: (patch: Partial<PriceDraft>) => void, size: "xs" | "default") =>
    (["material_cost", "labor_cost"] as const).map((field) => (
      <Input
        key={field}
        aria-label={`${label} ${field === "material_cost" ? "자재" : "인건"} 원가`}
        className="text-right tabular-nums"
        disabled={editsOn}
        inputMode="numeric"
        inputSize={size}
        name={`${name}-${field}`}
        placeholder={field === "material_cost" ? "자재" : "인건"}
        value={draft[field] === null ? "" : formatNumber(draft[field] ?? 0)}
        onChange={(event) => {
          const next = costInput(event.target.value);
          if (overPriceMax(next)) return;
          // 원가 × 마진 판매가가 상한을 넘는 원가는 받지 않는다 — 칸은 이전 값.
          if (saleTooBig(trade, { ...draft, [field]: next }, margins)) {
            toast.error(saleOverMaxText(label), { id: "price-sale-over-max" });
            return;
          }
          onPatch({ [field]: next });
        }}
      />
    ));

  // 판매가 — 원가가 있거나 포함 보기면 읽기만(계산값·환산값), 아니면 두 칸(자재·노무 단가).
  const saleCell = (trade: string, label: string, name: string, draft: PriceDraft, onPatch: (patch: Partial<PriceDraft>) => void, size: "xs" | "default") => {
    const sale = saleOf(trade, draft, margins, vatView);
    if (costTotalOf(draft) === null && vatView === "EXCL") {
      return (["material", "labor"] as const).map((field) => (
        <Input
          key={field}
          aria-label={`${label} ${field === "material" ? "자재" : "노무"} 단가`}
          className="text-right tabular-nums"
          disabled={editsOn}
          inputMode="numeric"
          inputSize={size}
          name={`${name}-${field}`}
          value={formatNumber(draft[field])}
          onChange={(event) => {
            const next = parseMoney(event.target.value);
            if (!overPriceMax(next)) onPatch({ [field]: next });
          }}
        />
      ));
    }
    return [
      <span key="sale" className="block text-right font-medium tabular-nums" data-price-sale={costTotalOf(draft) === null ? "converted" : "calc"} title={costTotalOf(draft) === null ? undefined : "원가 × (1 + 마진)"}>
        <Money value={sale.material_unit_price + sale.labor_unit_price} />
      </span>,
      <span key="split" className="block text-right text-xs tabular-nums text-muted-foreground">
        자재 <Money value={sale.material_unit_price} /> · 노무 <Money value={sale.labor_unit_price} />
      </span>,
    ];
  };

  const profitNote = (trade: string, draft: PriceDraft) =>
    costTotalOf(draft) === null ? null : (
      <span className="block text-right text-xs tabular-nums text-muted-foreground">
        이익률 {profitText(appliedMarginBp({ trade, margin_bp: draft.margin_bp }, margins))}%
      </span>
    );

  const catalogCell = (row: PriceRow) => (
    <CatalogPriceCell
      busy={catalogBusy}
      canEdit={canEdit}
      row={row}
      onOpenSubmit={() => setSubmitTarget({ id: row.id, item: row.item, scope: row.scope, tradeName: resolveTradeName(trades, row.trade), unit: row.unit })}
      onRun={runCatalog}
    />
  );

  const priceCard = (row: PriceRow) => {
    const draft = draftFor(row);
    const base = draftOf(row);
    const costText = (value: number | null) => (value === null ? "없음" : formatWon(value));
    const moved = [
      draft.material_cost !== base.material_cost && `자재 원가 ${costText(base.material_cost)} → ${costText(draft.material_cost)}`,
      draft.labor_cost !== base.labor_cost && `인건 원가 ${costText(base.labor_cost)} → ${costText(draft.labor_cost)}`,
      draft.margin_bp !== base.margin_bp && `마진 ${base.margin_bp === null ? "기본" : signed(base.margin_bp)} → ${draft.margin_bp === null ? "기본" : signed(draft.margin_bp)}`,
      costTotalOf(draft) === null && draft.material !== base.material && `자재 ${formatWon(base.material)} → ${formatWon(draft.material)}`,
      costTotalOf(draft) === null && draft.labor !== base.labor && `노무 ${formatWon(base.labor)} → ${formatWon(draft.labor)}`,
    ].filter((v): v is string => Boolean(v));
    const patch = (next: Partial<PriceDraft>) => onEdit(row, next);
    return (
      <SectionCard
        key={row.id}
        as="li"
        className={cn("flex flex-col gap-y-2.5", moved.length > 0 && "border-primary")}
        data-price-card={moved.length > 0 ? "changed" : undefined}>
        <div className="min-w-0">
          <p className="break-words text-xs text-muted-foreground">
            {resolveTradeName(trades, row.trade)}
            {row.scope && ` · ${row.scope}`}
          </p>
          <div className="mt-0.5 flex items-start justify-between gap-x-3">
            <p className="min-w-0 break-words font-semibold">{row.item}</p>
            <span className="shrink-0 text-xs text-muted-foreground">{row.unit || EMPTY_MARK}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {costTotalOf(draft) === null && vatView === "EXCL" ? (
            saleCell(row.trade, row.item, `price-${row.id}`, draft, patch, "default").map((input, i) => (
              <div key={i} className="flex min-w-0 flex-col gap-y-1">
                <span aria-hidden className="text-xs text-muted-foreground">{i === 0 ? "자재 단가" : "노무 단가"}</span>
                {input}
              </div>
            ))
          ) : (
            <div className="col-span-2 flex items-baseline justify-between gap-x-3">
              <span className="text-xs text-muted-foreground">판매가 ({vatBasisLabel(vatView)})</span>
              <div className="min-w-0">{saleCell(row.trade, row.item, `price-${row.id}`, draft, patch, "default")}</div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {costInputs(row.trade, row.item, `price-${row.id}`, draft, patch, "default").map((input, i) => (
            <div key={i} className="flex min-w-0 flex-col gap-y-1">
              <span aria-hidden className="text-xs text-muted-foreground">{i === 0 ? "자재 원가" : "인건 원가"}</span>
              {input}
            </div>
          ))}
          <div className="flex min-w-0 flex-col gap-y-1">
            <span aria-hidden className="text-xs text-muted-foreground">마진 %</span>
            <MarginInput
              allowEmpty
              bp={draft.margin_bp}
              disabled={editsOn}
              inputSize="default"
              label={`${row.item} 마진`}
              name={`price-${row.id}-margin`}
              placeholder={fallbackText(row.trade)}
              reject={(bp) => (saleTooBig(row.trade, { ...draft, margin_bp: bp }, margins) ? saleOverMaxText(row.item) : null)}
              onChange={(bp) => patch({ margin_bp: bp })}
            />
            {profitNote(row.trade, draft)}
          </div>
        </div>
        <div className="flex items-start justify-between gap-x-3" data-price-card-catalog>
          <span className="shrink-0 text-xs text-muted-foreground">표준 상품</span>
          {catalogCell(row)}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
          <span className="tabular-nums text-muted-foreground">
            {row.last_used_at ? `마지막 사용 ${formatDate(row.last_used_at)} · ${row.used_count}회` : "아직 쓰지 않음"}
          </span>
          {moved.length > 0 && <span className="font-medium tabular-nums text-primary">바뀜 {moved.join(" · ")}</span>}
        </div>
      </SectionCard>
    );
  };

  const tradeChoices = trades.filter((trade) => margins.trades[trade.code] === undefined);

  return (
    <div className="flex flex-col gap-y-3">
      <SectionCard className="flex flex-col gap-y-3" data-testid="price-margins">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <Field className="w-32" label="회사 기본 마진 %">
            <MarginInput
              allowEmpty={false}
              bp={margins.default_bp}
              disabled={marginsOff}
              inputSize="default"
              label="회사 기본 마진"
              name="margin-default"
              reject={(bp) => marginReject({ ...margins, default_bp: bp ?? margins.default_bp })}
              onChange={(bp) => setMargins({ ...margins, default_bp: bp ?? margins.default_bp })}
            />
          </Field>
          <p className="pb-2 text-sm tabular-nums text-muted-foreground" data-testid="price-margin-default">
            회사 기본 마진 {signed(margins.default_bp)} (= 이익률 {profitText(margins.default_bp)}%)
            <InfoTip label="마진 안내">
              원가를 넣은 품목의 판매가 = 원가 × (1 + 마진). 품목 마진이 비면 공종 마진, 그것도 없으면 회사 기본을 씁니다.
            </InfoTip>
          </p>
        </div>
        <div className="flex flex-col gap-y-2">
          <span className="text-xs text-muted-foreground">공종별 마진</span>
          {Object.keys(margins.trades).length === 0 ? (
            <p className="text-sm text-muted-foreground">공종별 마진이 없습니다 — 모든 공종에 회사 기본을 씁니다.</p>
          ) : (
          <ul className="flex flex-wrap gap-2">
            {Object.entries(margins.trades).map(([code, bp]) => (
              <li key={code} className="flex items-center gap-x-1.5 rounded-md border border-border px-2 py-1" data-price-trade-margin={code}>
                <span className="text-sm">{resolveTradeName(trades, code)}</span>
                <div className="w-20">
                  <MarginInput
                    allowEmpty={false}
                    bp={bp}
                    disabled={marginsOff}
                    inputSize="xs"
                    label={`${resolveTradeName(trades, code)} 공종 마진`}
                    name={`margin-trade-${code}`}
                    reject={(next) => marginReject({ ...margins, trades: { ...margins.trades, [code]: next ?? bp } })}
                    onChange={(next) => setMargins({ ...margins, trades: { ...margins.trades, [code]: next ?? bp } })}
                  />
                </div>
                <span className="text-xs tabular-nums text-muted-foreground">이익률 {profitText(bp)}%</span>
                {canEditMargins && (
                  <Button
                    aria-label={`${resolveTradeName(trades, code)} 공종 마진 지우기`}
                    disabled={pending}
                    size="icon-xs"
                    type="button"
                    variant="ghost-destructive"
                    onClick={() => {
                      const rest = { ...margins.trades };
                      delete rest[code];
                      setMargins({ ...margins, trades: rest });
                    }}>
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
          )}
          {canEditMargins && tradeChoices.length > 0 && (
            <SimpleSelect
              aria-label="공종 마진 추가"
              className="w-auto min-w-40 self-start"
              disabled={pending}
              options={[
                { value: "", label: "공종 마진 추가" },
                ...tradeChoices.map((trade) => ({ value: trade.code, label: trade.name })),
              ]}
              size="sm"
              value=""
              onValueChange={(code) => {
                if (code !== "") setMargins({ ...margins, trades: { ...margins.trades, [code]: margins.default_bp } });
              }}
            />
          )}
        </div>
      </SectionCard>

      <FilterBar action="/app/settings">
        <input name="tab" type="hidden" value="prices" />
        {linkFilter !== "all" && <input name="link" type="hidden" value={linkFilter} />}

        <SearchField
          aria-label="단가 검색"
          defaultValue={filter.q}
          name="q"
          placeholder="범위 · 항목"
        />

        {/* URL 의 공종만 바뀌는 이동(뒤로가기)에도 옛 선택이 남지 않게 다시 그린다. */}
        <SimpleSelect
          key={filter.trade}
          aria-label="공종"
          className="w-auto min-w-32"
          defaultValue={filter.trade}
          name="trade"
          options={[
            { value: "", label: "공종 전체" },
            ...trades.map((trade) => ({ value: trade.code, label: trade.name })),
          ]}
          size="sm"
        />

        <Button size="sm" type="submit" variant="outline">
          검색
        </Button>

      </FilterBar>

      {catalogCounts && (
        <FilterChips
          activeHref={priceFilterHref(filter, linkFilter)}
          aria-label="표준 상품 연결"
          items={(["all", "linked", "unlinked", "pending"] as const).map((link) => ({
            label: LINK_FILTER_LABEL[link],
            href: priceFilterHref(filter, link),
            count: catalogCounts[link],
          }))}
        />
      )}

      {catalogCounts && catalogCounts.suggested > 0 && !suggestHidden && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground" data-testid="catalog-suggest-notice" role="status">
          <span className="min-w-0 flex-1">연결 제안 {catalogCounts.suggested}개 — 표준 상품에 같은 상품이 있습니다.</span>
          <Link className="font-medium text-primary underline-offset-4 hover:underline" href={priceFilterHref(filter, "unlinked")}>
            제안 보기
          </Link>
          <Button size="xs-touch" type="button" variant="link-muted" onClick={() => setSuggestHidden(true)}>
            나중에
          </Button>
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className="flex flex-wrap items-center gap-x-1 text-sm text-muted-foreground" data-testid="price-vat-basis">
          단가는 모두 {vatBasisLabel("EXCL")}(공급가)입니다.
          <InfoTip label="단가표 부가세 기준 안내">
            부가세 포함 견적에는 ×1.1 해서 제안하고, 포함 견적을 보내면 ÷1.1 해서 쌓습니다. 원가도 공급가로 적습니다.
          </InfoTip>
        </p>
        <div aria-label="판매가 보기" className="flex flex-wrap gap-1.5" role="group">
          {(["EXCL", "INCL"] as const).map((mode) => (
            <Button key={mode} aria-pressed={vatView === mode} size="chip" type="button" variant="chip" onClick={() => setVatView(mode)}>
              {vatBasisLabel(mode)}
            </Button>
          ))}
        </div>
      </div>

      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          대표만 단가를 고칠 수 있습니다.
        </p>
      )}

      {canEdit && (
        <div className="flex flex-col gap-y-2">
          {added.map((row) => {
            const patch = (next: Partial<PriceDraft>) => onEditAdded(row.key, next);
            const label = `새 품목 ${added.indexOf(row) + 1}`;
            return (
              <SectionCard key={row.key} as="div" className="flex flex-col gap-y-2" data-price-new={row.key}>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <Field label="공종">
                    <SimpleSelect
                      aria-label={`${label} 공종`}
                      disabled={pending}
                      options={trades.map((trade) => ({ value: trade.code, label: trade.name }))}
                      value={row.trade}
                      onValueChange={(code) => onEditAdded(row.key, { trade: code })}
                    />
                  </Field>
                  <Field label="범위">
                    <Input aria-label={`${label} 범위`} disabled={pending} name={`${row.key}-scope`} value={row.scope} onChange={(event) => onEditAdded(row.key, { scope: event.target.value })} />
                  </Field>
                  <Field label="항목">
                    <Input aria-label={`${label} 항목`} disabled={pending} name={`${row.key}-item`} value={row.item} onChange={(event) => onEditAdded(row.key, { item: event.target.value })} />
                  </Field>
                  <Field label="단위">
                    <Input aria-label={`${label} 단위`} disabled={pending} name={`${row.key}-unit`} value={row.unit} onChange={(event) => onEditAdded(row.key, { unit: event.target.value })} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {costInputs(row.trade, label, row.key, row, patch, "default").map((input, i) => (
                    <div key={i} className="flex min-w-0 flex-col gap-y-1">
                      <span aria-hidden className="text-xs text-muted-foreground">{i === 0 ? "자재 원가" : "인건 원가"}</span>
                      {input}
                    </div>
                  ))}
                  <div className="flex min-w-0 flex-col gap-y-1">
                    <span aria-hidden className="text-xs text-muted-foreground">마진 %</span>
                    <MarginInput
                      allowEmpty
                      bp={row.margin_bp}
                      disabled={pending}
                      inputSize="default"
                      label={`${label} 마진`}
                      name={`${row.key}-margin`}
                      placeholder={fallbackText(row.trade)}
                      reject={(bp) => (saleTooBig(row.trade, { ...row, margin_bp: bp }, margins) ? saleOverMaxText(label) : null)}
                      onChange={(bp) => patch({ margin_bp: bp })}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-y-1">
                    <span aria-hidden className="text-xs text-muted-foreground">판매가 ({vatBasisLabel(vatView)})</span>
                    {saleCell(row.trade, label, row.key, row, patch, "default")}
                  </div>
                </div>
                <Button
                  className="self-end"
                  disabled={pending}
                  size="sm"
                  type="button"
                  variant="ghost-destructive"
                  onClick={() => setAdded((prev) => prev.filter((other) => other.key !== row.key))}>
                  <Trash2 className="size-3.5" />빼기
                </Button>
              </SectionCard>
            );
          })}
          <Button className="self-start" disabled={pending || trades.length === 0} size="sm" type="button" variant="outline" onClick={onAddRow}>
            <Plus className="size-3.5" />품목 추가
          </Button>
        </div>
      )}

      {phone !== true && (
        <ListTable
          className="hidden md:block"
          columns={[
            { label: "공종" },
            { label: "범위 · 항목", wrap: true },
            { label: "단위" },
            { label: "원가(자재·인건)", align: "right" },
            { label: "기본 마진", align: "right" },
            { label: `판매가(${vatView === "EXCL" ? "별도" : "포함"})`, align: "right" },
            { label: "표준 상품" },
            { label: "사용 횟수", align: "right" },
            { label: "마지막 사용", align: "right" },
          ]}
          empty="조건에 맞는 단가가 없습니다.">
          {rows.map((row) => {
            const draft = draftFor(row);
            const patch = (next: Partial<PriceDraft>) => onEdit(row, next);
            return (
              <TableRow key={row.id} data-price-row={changedIds.has(row.id) ? "changed" : undefined}>
                <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {resolveTradeName(trades, row.trade)}
                </TableCell>
                <TableCell className="px-3 py-2">
                  {row.scope && <span className="block text-xs text-muted-foreground">{row.scope}</span>}
                  <span className="font-medium">{row.item}</span>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {row.unit || EMPTY_MARK}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-right">
                  <div className="ml-auto flex w-24 flex-col gap-y-1">{costInputs(row.trade, row.item, `price-${row.id}`, draft, patch, "xs")}</div>
                </TableCell>
                <TableCell className="px-3 py-1.5 text-right">
                  <div className="ml-auto w-20">
                    <MarginInput
                      allowEmpty
                      bp={draft.margin_bp}
                      disabled={editsOn}
                      inputSize="xs"
                      label={`${row.item} 마진`}
                      name={`price-${row.id}-margin`}
                      placeholder={fallbackText(row.trade)}
                      reject={(bp) => (saleTooBig(row.trade, { ...draft, margin_bp: bp }, margins) ? saleOverMaxText(row.item) : null)}
                      onChange={(bp) => patch({ margin_bp: bp })}
                    />
                    {profitNote(row.trade, draft)}
                  </div>
                </TableCell>
                <TableCell className="px-3 py-1.5 text-right">
                  <div className="flex flex-col gap-y-1">
                    {saleCell(row.trade, row.item, `price-${row.id}`, draft, patch, "xs").map((cell, i) => (
                      <div key={i} className="ml-auto w-28">{cell}</div>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="px-3 py-1.5">
                  <div className="w-32">{catalogCell(row)}</div>
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {row.used_count}회
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {formatDate(row.last_used_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </ListTable>
      )}

      {/* 폰(md 미만) — 공종별 접기 · 펼치면 줄마다 카드(공종·범위 / 항목·단위 / 판매가 / 원가·마진 / 마지막 사용) */}
      {phone !== false && (
        <>
          <p className="text-xs text-muted-foreground md:hidden">
            {rows.length}개 · 공종 {groups.length}개
          </p>
          <ul className="flex flex-col gap-y-2.5 md:hidden" data-testid="price-cards">
            {rows.length === 0 && (
              <SectionCard as="li" className="text-center text-sm text-muted-foreground">
                조건에 맞는 단가가 없습니다.
              </SectionCard>
            )}
            {groups.map((group) => {
              const changedCount = group.rows.filter((row) => changedIds.has(row.id)).length;
              const open = openTrades[group.trade] ?? (expandAll || changedCount > 0);
              return (
                <li key={group.trade} className="flex flex-col gap-y-2" data-price-trade={group.trade}>
                  <Button
                    aria-expanded={open}
                    className="flex min-h-10 w-full items-center justify-start text-left"
                    size="row"
                    variant="card"
                    onClick={() =>
                      setFolds((prev) => ({
                        key: foldKey,
                        open: { ...foldsFor(prev, foldKey), [group.trade]: !open },
                      }))
                    }>
                    {open ? (
                      <ChevronDown className="shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate font-semibold">{resolveTradeName(trades, group.trade)}</span>
                    {changedCount > 0 && (
                      <span className="shrink-0 text-xs font-medium tabular-nums text-primary">바뀜 {changedCount}</span>
                    )}
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{group.rows.length}개</span>
                  </Button>
                  {open && <ul className="flex flex-col gap-y-2.5">{group.rows.map(priceCard)}</ul>}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {canEdit && (
        <CatalogSubmitDialog
          target={submitTarget}
          onDone={() => router.refresh()}
          onOpenChange={(open) => {
            if (!open) setSubmitTarget(null);
          }}
        />
      )}

      {canEdit && (
        <SaveBar
          blocked={blocked}
          dirty={dirty}
          label={changed.length + added.length > 0 ? `${changed.length + added.length}줄 저장` : "저장"}
          onSave={onSave}
          pending={pending}
        />
      )}
    </div>
  );
};

export default SettingsPrices;
